import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { getQuizBySlug, submitQuiz } from "../../features/courses/courseSlice";
import { FiCheckCircle } from "react-icons/fi";
import QuizQuestion from "./QuizQuestion";
import QuizResults from "./QuizResults";

function QuizEmbed({
  quizSlug,
  quizNumber,
  isActive,
  onToggle,
  onProgressUpdate,
}) {
  const dispatch = useDispatch();

  const [quiz, setQuiz] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [progress, setProgress] = useState(null);
  const [answers, setAnswers] = useState({}); // { questionId: [selectedIds] }
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    const fetchQuiz = async () => {
      try {
        const result = await dispatch(getQuizBySlug(quizSlug)).unwrap();
        setQuiz(result.data.quiz);
        setQuestions(result.data.questions);
        setProgress(result.data.progress);

        // Initialize answers from previous submission if exists
        if (result.data.progress?.quizAnswers) {
          // Convert Map to object if needed
          const savedAnswers = result.data.progress.quizAnswers;
          if (savedAnswers instanceof Map) {
            setAnswers(Object.fromEntries(savedAnswers));
          } else if (typeof savedAnswers === "object") {
            setAnswers(savedAnswers);
          }
        }

        if (onProgressUpdate && result.data.progress) {
          onProgressUpdate(quizSlug, result.data.progress);
        }
      } catch (err) {
        console.error("Error fetching quiz:", err);
      }
    };

    fetchQuiz();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizSlug, dispatch]);

  const handleAnswerChange = (questionId, selectedAnswers) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: selectedAnswers,
    }));
    // Clear previous results when answers change
    setResults(null);
  };

  const handleSubmit = async () => {
    if (!quiz) return;

    setIsSubmitting(true);
    try {
      const result = await dispatch(
        submitQuiz({
          quizId: quiz._id,
          answers,
        })
      ).unwrap();

      setResults(result.data);
      setProgress(result.submissionResults);

      if (onProgressUpdate) {
        onProgressUpdate(quizSlug, result.submissionResults);
      }
    } catch (err) {
      console.error("Error submitting quiz:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetry = () => {
    setResults(null);
  };

  if (!quiz) {
    return (
      <div
        className="rounded-lg p-4"
        style={{
          background: "rgba(39, 39, 42, 0.5)",
          border: "1px solid rgba(113, 113, 122, 0.3)",
        }}
      >
        <p className="text-sm text-gray-400">Loading quiz...</p>
      </div>
    );
  }

  const isCompleted = progress?.status === "Completed";

  // Calculate answered questions count
  const answeredCount = Object.keys(answers).filter(
    (qId) => answers[qId] && answers[qId].length > 0
  ).length;
  const totalQuestions = questions.length;

  return (
    <div
      className="quiz-card rounded-xl overflow-hidden transition-all"
      style={{
        background: isActive
          ? "rgba(39, 39, 42, 0.9)"
          : "rgba(39, 39, 42, 0.4)",
        border: isActive
          ? "1px solid rgba(113, 113, 122, 0.5)"
          : "1px solid rgba(113, 113, 122, 0.3)",
        boxShadow: isActive
          ? "0 4px 12px rgba(0, 0, 0, 0.3)"
          : "0 2px 8px rgba(0, 0, 0, 0.3)",
      }}
    >
      {/* Quiz Header - Collapsible */}
      <div
        className="px-4 py-3 cursor-pointer transition-all"
        style={{
          background: isActive
            ? "rgba(24, 24, 27, 0.6)"
            : "rgba(24, 24, 27, 0.5)",
        }}
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span
              className="text-xs font-bold px-2 py-0.5 rounded"
              style={{
                background: "rgba(165, 138, 194, 0.2)",
                color: "rgba(200, 180, 220, 1)",
              }}
            >
              Quiz {quizNumber}
            </span>
            {isCompleted && (
              <FiCheckCircle
                className="w-4 h-4 flex-shrink-0"
                style={{ color: "#86b27a" }}
                title="Completed"
              />
            )}
            <h3 className="text-sm font-semibold text-white truncate">
              {quiz.name}
            </h3>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {isCompleted && (
              <span
                className="text-xs px-2 py-1 rounded font-medium"
                style={{
                  background: "rgba(134, 178, 122, 0.2)",
                  color: "rgba(184, 212, 168, 1)",
                  border: "1px solid rgba(134, 178, 122, 0.3)",
                }}
              >
                Passed
              </span>
            )}
            <span className="text-zinc-500 text-sm">
              {isActive ? "▼" : "▶"}
            </span>
          </div>
        </div>
        {/* Progress Info Bar */}
        {progress && !isActive && (
          <div className="mt-2 flex items-center gap-3 text-xs text-zinc-400">
            <span
              className={isCompleted ? "" : "text-zinc-400"}
              style={isCompleted ? { color: "#86b27a" } : {}}
            >
              {progress.status || "Not Started"}
            </span>
            {progress.attemptCounter > 0 && (
              <>
                <span>•</span>
                <span>
                  {progress.attemptCounter} attempt
                  {progress.attemptCounter !== 1 ? "s" : ""}
                </span>
              </>
            )}
          </div>
        )}
      </div>

      {/* Expanded Content */}
      {isActive && (
        <div
          className="border-t"
          style={{ borderColor: "rgba(113, 113, 122, 0.3)" }}
        >
          <div className="p-4">
            {quiz.description && (
              <p className="text-sm text-zinc-400 mb-4">{quiz.description}</p>
            )}

            {results ? (
              <QuizResults results={results} onRetry={handleRetry} />
            ) : (
              <>
                {questions.map((question, index) => (
                  <QuizQuestion
                    key={question._id}
                    question={question}
                    questionNumber={index + 1}
                    selectedAnswers={answers[question._id] || []}
                    onAnswerChange={(selected) =>
                      handleAnswerChange(question._id, selected)
                    }
                    disabled={isCompleted}
                  />
                ))}

                <div className="mt-4 flex items-center justify-between">
                  <span className="text-xs text-zinc-400">
                    {answeredCount} of {totalQuestions} answered
                  </span>
                  <button
                    onClick={handleSubmit}
                    disabled={
                      isSubmitting ||
                      isCompleted ||
                      answeredCount < totalQuestions
                    }
                    className="px-6 py-2 rounded-lg font-medium text-sm text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      background: isSubmitting
                        ? "rgba(165, 138, 194, 0.4)"
                        : "linear-gradient(135deg, rgba(165, 138, 194, 0.7), rgba(145, 118, 174, 0.7))",
                      border: "1px solid rgba(165, 138, 194, 0.4)",
                    }}
                  >
                    {isSubmitting
                      ? "Submitting..."
                      : isCompleted
                      ? "Quiz Passed"
                      : "Submit Quiz"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default QuizEmbed;
